import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  CURRENT_BETA_AGREEMENT_VERSION,
} from '@/legal/versions';



const Auth = () => {
  const navigate = useNavigate();
  // FIX-R32-H-2/H-3: route signOut through AuthContext so localStorage
  // (activeShopId, chronicle_selected_context, joinTeamToken, tutorial flag)
  // is scrubbed in one place.
  const { user, signOut: contextSignOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  
  // Beta invite flow
  const isBetaFlow = searchParams.get('beta') === '1';
  const betaEmail = searchParams.get('email');
  
  // Password recovery states
  const [showForgotPanel, setShowForgotPanel] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Legal acceptance states
  const [accountType, setAccountType] = useState<'shop' | 'passport'>('shop');
  const [betaChecked, setBetaChecked] = useState(false);

  const allLegalChecked = betaChecked;


  // Forward any ?invitation= to /join-team
  useEffect(() => {
    const invitationParam = searchParams.get('invitation');
    if (invitationParam) {
      navigate(`/join-team?token=${invitationParam}`, { replace: true });
      return;
    }
  }, [searchParams, navigate]);

  // Detect recovery mode from URL params
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(hash.indexOf('?') + 1));
    const hashType = hashParams.get('type');
    
    if (typeParam === 'recovery' || hashType === 'recovery') {
      setIsRecoveryMode(true);
    }
  }, [searchParams]);

  // Auto-fill reset email when forgot panel opens
  useEffect(() => {
    if (showForgotPanel && email) {
      setResetEmail(email);
    }
  }, [showForgotPanel, email]);
  
  // Beta flow: pre-fill email from query param
  useEffect(() => {
    if (isBetaFlow && betaEmail) {
      setEmail(decodeURIComponent(betaEmail).toLowerCase().trim());
    }
  }, [isBetaFlow, betaEmail]);

  // Beta flow: redirect to complete after login
  useEffect(() => {
    if (user && isBetaFlow) {
      navigate('/beta/complete');
    }
  }, [user, isBetaFlow, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: 'Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Welcome back!',
          description: 'You have successfully signed in.',
        });
        // Beta flow redirects to /beta/complete, otherwise normal flow
        if (isBetaFlow) {
          navigate('/beta/complete');
        } else {
          navigate('/');
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please ensure both passwords are identical.',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const redirectUrl = isBetaFlow
        ? `${window.location.origin}/beta/complete`
        : `${window.location.origin}/auth/confirm`;
      
      const { error, data } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            account_type: accountType,
          },
        },
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast({
            title: 'Account exists',
            description: 'This email is already registered. Please sign in instead.',
            variant: 'destructive',
          });
        } else if (error.message.includes('Alpha testing access required')) {
          toast({
            title: 'Alpha Testing Access Required',
            description: 'This email is not approved for alpha testing. Please contact support for access.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Sign Up Failed',
            description: error.message,
            variant: 'destructive',
          });
        }
      } else {
        // Insert beta ack acceptance with shop_id = NULL (user has no shop yet)
        const userId = data.user?.id;
        if (userId) {
          const ua = navigator.userAgent || 'unknown';

          const acceptances = [
            { user_id: userId, agreement_type: 'beta_agreement_ack', agreement_version: CURRENT_BETA_AGREEMENT_VERSION, user_agent: ua },
          ];

          // Idempotent insert with retry. FIX-R32-CRIT-2: AWAIT the upsert
          // before navigating so the user doesn't land on /onboard without
          // the beta_agreement_ack row and get trapped in a later legal gate.
          const upsertAcceptances = async (attempt = 1): Promise<void> => {
            const { error: insertErr } = await supabase
              .from('legal_acceptances')
              .upsert(acceptances, { onConflict: 'user_id,agreement_type,agreement_version', ignoreDuplicates: true });
            if (insertErr) {
              console.error(`Failed to record legal acceptances (attempt ${attempt}):`, insertErr);
              if (attempt < 2) {
                await new Promise(r => setTimeout(r, 1000));
                return upsertAcceptances(attempt + 1);
              }
              toast({
                title: 'Notice',
                description: 'Legal acceptance could not be recorded. Please contact support.',
                variant: 'destructive',
              });
            }
          };
          await upsertAcceptances();
        }

        // FIX-R128-HIGH-1: when email confirmation is required (Supabase
        // autoconfirm OFF — the secure default), `auth.signUp` returns
        // `data.session === null` and the user has NO active session yet.
        // Previously this code unconditionally `navigate('/onboard')` —
        // but Onboard.tsx renders "Loading..." while `!user`, with no
        // recovery action, so the user landed on a permanent Loading
        // screen with no instruction to check their email. Beta flow has
        // its own session check (BetaComplete redirects to /auth if no
        // session), so its existing navigate is safe; the standard flow
        // needs to branch on session presence.
        const hasSession = !!data?.session;

        if (isBetaFlow) {
          toast({
            title: 'Account created!',
            description: 'Setting up your shop...',
          });
          navigate('/beta/complete');
        } else if (hasSession) {
          toast({
            title: 'Account created!',
            description: 'Account created. Let\'s get you set up.',
          });
          navigate('/onboard');
        } else {
          // No session yet — email confirmation required. Stay on /auth and
          // tell the user to check their inbox. The confirmation link
          // routes through /auth/confirm which establishes the session
          // then sends them on to /onboard.
          toast({
            title: 'Check your email',
            description: `We sent a confirmation link to ${email}. Click it to finish setting up your account.`,
            duration: 10000,
          });
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!resetEmail) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    setIsResetting(true);
    setResetEmailSent(false);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/confirm?type=recovery`,
      });

      if (error) {
        toast({
          title: 'Reset Email Failed',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Reset email sent',
          description: 'Check your inbox for a password reset link.',
        });
        setResetEmailSent(true);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Password mismatch',
        description: 'Passwords do not match. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Password updated',
          description: 'You can now sign in with your new password.',
        });

        // After resetting the password, sign the user out so they return to the
        // standard Sign In / Sign Up view instead of the account management card.
        await contextSignOut();

        setNewPassword('');
        setConfirmNewPassword('');
        setIsRecoveryMode(false);
        navigate('/auth', { replace: true });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {isBetaFlow ? 'Create Your Account' : 'OptiBayAI'}
          </CardTitle>
          <CardDescription>
            {isBetaFlow
              ? 'Set up your account to access OptiBay AI'
              : user 
                ? 'Account Management'
                : 'Auto Shop Scheduler'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isRecoveryMode ? (
            // Password Recovery Mode
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Reset Your Password</h3>
                <p className="text-sm text-muted-foreground">
                  Enter your new password below.
                </p>
              </div>
              
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <PasswordInput
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <PasswordInput
                    id="confirm-new-password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
                
                <p className="text-xs text-muted-foreground">
                  Forgot username? Your OptiBayAI username is the email you signed up with. 
                  If you no longer have access to that email, contact your shop admin.
                </p>
              </form>
            </div>
          ) : user ? (
            // Show account options when user is logged in
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-4 text-sm">
                <p className="font-medium mb-2">Currently signed in as:</p>
                <p className="text-muted-foreground">{user.email}</p>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={async () => {
                    await contextSignOut();
                    setEmail('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="w-full"
                >
                  Sign Out
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                  Back to Dashboard
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue={isBetaFlow ? "signup" : "signin"} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4" noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    {/* autoComplete pairs with the `username` field above so
                        password managers fill the correct credential and Chrome
                        stops emitting the "Input elements should have
                        autocomplete attributes" DOM warning. `current-password`
                        (not `new-password`) is what marks this as the sign-in
                        credential rather than one being created. */}
                    <PasswordInput
                      id="signin-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setShowForgotPanel(!showForgotPanel)}
                      className="text-sm text-blue-700 dark:text-blue-400 hover:underline cursor-pointer"
                    >
                      Forgot username or password?
                    </button>
                    
                    {showForgotPanel && (
                      <div className="border rounded-lg p-4 mt-4 bg-muted/50 space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Your username is your email address. Enter it below and we'll send you a reset link.
                        </p>
                        
                        <div className="space-y-2">
                          <Label htmlFor="reset-email">Email</Label>
                          <Input
                            id="reset-email"
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            placeholder="you@example.com"
                          />
                        </div>
                        
                        <Button
                          type="button"
                          onClick={handleSendResetEmail}
                          disabled={isResetting || !resetEmail}
                          className="w-full"
                        >
                          {isResetting ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                        
                        {resetEmailSent && (
                          <p className="text-sm text-green-600 dark:text-green-400">
                            Reset email sent. Check your inbox (and spam folder).
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isBetaFlow}
                      autoComplete="username"
                    />
                    {isBetaFlow && (
                      <p className="text-xs text-muted-foreground">
                        This email is from your beta invite
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    {/* `new-password` on BOTH signup fields: it tells the
                        browser this is a credential being created, which
                        suppresses autofill of the existing password and lets
                        password managers offer generation. Using
                        `current-password` here would make managers overwrite
                        the confirm field with the saved credential. */}
                    <PasswordInput
                      id="signup-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <PasswordInput
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                  </div>

                  {/* Account Type */}
                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <Label className="text-sm font-medium">Account Type</Label>
                    <RadioGroup
                      value={accountType}
                      onValueChange={(v) => setAccountType(v as 'shop' | 'passport')}
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="shop" id="account-shop" />
                        <Label htmlFor="account-shop" className="font-normal cursor-pointer">
                          OptiBay Shop Account
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="passport" id="account-passport" />
                        <Label htmlFor="account-passport" className="font-normal cursor-pointer">
                          OptiBay Passport Account
                        </Label>
                      </div>
                    </RadioGroup>
                    {/* Core-100 direction 10: creator and school accounts are requested, then approved. */}
                    <p className="text-xs text-muted-foreground" data-testid="request-account-hint">
                      Training provider or school? Create an account, then request a creator or school account at{' '}
                      <a className="underline" href="/request-account">Request an account</a>.
                    </p>
                  </div>

                  {/* Legal Checkbox */}
                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <Label className="text-sm font-medium">Legal Agreements</Label>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="beta-check"
                        checked={betaChecked}
                        onCheckedChange={(c) => setBetaChecked(c === true)}
                      />
                      <Label htmlFor="beta-check" className="text-sm font-normal leading-snug cursor-pointer">
                        I understand that OptiBay is currently in beta and I will electronically sign the{' '}
                        <a
                          href="/legal/beta-agreement"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline hover:text-primary/80"
                        >
                          Beta Agreement
                        </a>
                      </Label>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading || !allLegalChecked}>
                    {isLoading ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
